import type { components } from '../../generated/fct';
import { ApiError, ApiRequest } from '../shared';
import { AddendumStatus } from './addendum.model';

export type Category = components['schemas']['QCCategoryEnum'];

export const CATEGORY = {
  BODY_COMPOSITION: 'body_composition',
  BRAIN_HEALTH_ASSESSMENT: 'brain_health_assessment',
  HEART_HEALTH_ASSESSMENT: 'heart_health_assessment',
} as const satisfies Record<string, Category>;

export type ItemType = components['schemas']['QCHandlerTypeEnum'];

export const ITEM_TYPE = {
  CWF_DOCUMENT: 'cwf_document',
  FCT_ONLY: 'fct_only',
} as const satisfies Record<string, ItemType>;

export type QualityControlItemStatus = components['schemas']['QCStatusEnum'];

export const QUALITY_CONTROL_ITEM_STATUS = {
  APPROVED: 'approved',
  INITIAL_CHECK: 'initial_check',
  INTERNAL_REJECTED: 'internal_rejected',
  PENDING: 'pending',
  PROCESSING_FAILED: 'processing_failed',
  PUBLISHED: 'published',
  REJECTED: 'rejected',
  UNAVAILABLE: 'unavailable',
} as const satisfies Record<string, QualityControlItemStatus>;

export const QUALITY_CONTROL_ITEM_KEY = {
  ASL: 'asl',
  BODY_COMPOSITION_REPORT: 'body_composition_report',
  MYOSTRAIN_REPORT: 'myostrain_heart_report',
  NEUROQUANT_REPORT: 'neuroquant_report',
} as const;

export type QualityControlItemKey =
  (typeof QUALITY_CONTROL_ITEM_KEY)[keyof typeof QUALITY_CONTROL_ITEM_KEY];

type GeneratedQualityControlItem = components['schemas']['QualityControlItemResponseSchema'];

export interface QualityControlCategoryItem extends GeneratedQualityControlItem {
  addendumStatus?: AddendumStatus;
}

export interface QualityControlCategoryItemWithUuid extends QualityControlCategoryItem {
  uuid: string;
}

export type QualityControlCategory = Omit<components['schemas']['QCCategoryGroup'], 'items'> & {
  items: QualityControlCategoryItemWithUuid[];
};

export type QualityControl = Omit<components['schemas']['QCOverviewResponse'], 'categories'> & {
  categories: QualityControlCategory[];
};

type GeneratedQualityControlItemDetailed =
  components['schemas']['QualityControlItemDetailedSchema'];

type QualityControlItemDetailedBase = GeneratedQualityControlItemDetailed &
  Required<Pick<GeneratedQualityControlItemDetailed, 'uuid'>> & {
    addendumStatus?: AddendumStatus;
  };

export interface CwfQualityControlItemWithDocument extends Omit<
  QualityControlItemDetailedBase,
  'documentContent' | 'documentFileType' | 'itemType'
> {
  documentContent: string;
  documentFileType: string;
  itemType: 'cwf_document';
}

export interface FctQualityControlItemWithDocument extends Omit<
  QualityControlItemDetailedBase,
  'documentContent' | 'documentFileType' | 'itemType'
> {
  documentContent: null;
  documentFileType: null;
  itemType: 'fct_only';
}

export type QualityControlItemWithDocument =
  CwfQualityControlItemWithDocument | FctQualityControlItemWithDocument;

export interface QualityControlPayload {
  studyId: string;
}

export type QualityControlRequest = ApiRequest<QualityControlPayload>;

export type QualityControlResponse = QualityControl;

export type QualityControlError = ApiError<unknown>;

export interface QualityControlItemPayload {
  itemId: string;
  studyId: string;
}

export type QualityControlItemRequest = ApiRequest<QualityControlItemPayload>;

export type QualityControlItemResponse = QualityControlItemWithDocument;

export type QualityControlItemError = ApiError<unknown>;

export type PatchQualityControlItemPayload = {
  internalNotes?: null | string;
  patientNotes?: null | string;
  status?: QualityControlItemStatus;
};

export interface PatchQualityControlItemRequest extends ApiRequest<
  Partial<PatchQualityControlItemPayload>
> {
  invalidationMetadata: {
    reportUuid?: string;
  };
  itemId: string;
  studyId: string;
}

export type PatchQualityControlItemResponse = QualityControlItemWithDocument;

export type PatchQualityControlItemError = ApiError<unknown>;
